import { useState } from 'react';
import { C, Badge } from './shared';

export default function AlignmentTab() {
  const [fileStates, setFileStates] = useState({});
  const [gateStates, setGateStates] = useState({1:false,2:false,3:false,4:false,5:false});

  const toggleFile = (id) => setFileStates(p => {
    const cur = p[id];
    if (!cur) return {...p, [id]: 'uploaded'};
    if (cur === 'uploaded') return {...p, [id]: 'confirmed'};
    return {...p, [id]: null};
  });

  const SESSION_FILES = [
    { id:1, name:'HEAD.md', loc:'.claude/project-knowledge/', critical:true, extract:'Exact HEAD commit · branch · smoke count · current session' },
    { id:2, name:'SESSION.md', loc:'.claude/', critical:true, extract:'What we are building · D3 signal · key files · prereqs' },
    { id:3, name:'sessions-active.js', loc:'src/data/', critical:true, extract:'currentSession id · smokeCount · sequence order' },
    { id:4, name:'smoke-test.sh', loc:'scripts/', critical:true, extract:'Current test count — must match HEAD.md smoke value' },
    { id:5, name:'SESSION_LOCK.md', loc:'.claude/', critical:true, extract:'STATUS must be AVAILABLE before starting any work' },
  ];

  const GATES = [
    { id:1, name:'FILE READ', rule:'Never patch any file without reading its complete content first. Not grepped. Not assumed.' },
    { id:2, name:'NO STR.REPLACE', rule:'Files over 50 lines → complete file output only. Never str.replace or content.replace.' },
    { id:3, name:'ONE FIX AT A TIME', rule:'Never write fix 2 until fix 1 is confirmed working on the live site.' },
    { id:4, name:'SESSION CLOSE', rule:'All managed files updated before sync commit. SESSION.md must point at NEXT session.' },
    { id:5, name:'HOTFIX LIMIT', rule:'Same file fails twice → STOP. Declare full file rewrite. No third attempt.' },
  ];

  const confirmedCritical = SESSION_FILES.filter(f=>f.critical).every(f=>fileStates[f.id]==='confirmed');
  const confirmedCount = Object.values(fileStates).filter(v=>v==='confirmed').length;

  return (
    <div style={{ display:'grid', gap:16 }}>

      {/* Partnership Contract — always visible */}
      <div style={{ background: C.card, borderRadius:10, padding:20,
        borderTop:'4px solid '+C.purple, border:'1px solid '+C.purple+'44' }}>
        <div style={{ color:C.purple, fontWeight:800, fontSize:14, marginBottom:12,
          letterSpacing:'0.08em' }}>PARTNERSHIP CONTRACT — PERMANENT</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[
            { n:'1 — Owner Role', t:'Final authority on everything. When owner says STOP — Claude stops. One word. No argument. No one more try.' },
            { n:'2 — Claude Role', t:'Full partner: product vision · engineering · trading strategy · SDLC. Not an order taker. Surfaces gaps proactively.' },
            { n:'3 — Gates Under Pressure', t:'Uncertain about any value → ask repo. Hotfix fails twice → stop. Owner says stop → stop completely.' },
            { n:'4 — D3 First', t:'MarketPulze is a signal generator for D3. Every feature feeds the signal chain or it waits. Rule 20 non-negotiable.' },
            { n:'5 — D3 Learns', t:'Personal strategies + 9 proven external strategies. Every loss logged. No weight changes without owner approval.' },
            { n:'6 — Strategy Expansion', t:'9 strategies are the start not the ceiling. Formal expansion review at Phase D+ after D3a/D3b validation.' },
          ].map(p => (
            <div key={p.n} style={{ background:C.surface, borderRadius:8, padding:12,
              border:'1px solid '+C.purple+'33' }}>
              <div style={{ color:C.purple, fontWeight:700, fontSize:11, marginBottom:4 }}>{p.n}</div>
              <div style={{ color:C.subtle, fontSize:11, lineHeight:1.5 }}>{p.t}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:10, padding:'8px 12px', background:C.warning+'11',
          borderRadius:6, border:'1px solid '+C.warning+'33', fontSize:11, color:C.warning }}>
          Full contract: .claude/CLAUDE_ALIGNMENT_PROTOCOL.md · Part 2
        </div>
      </div>

      {/* Session State */}
      <div style={{ background:C.card, borderRadius:10, padding:20, border:'1px solid '+C.border }}>
        <div style={{ color:C.info, fontWeight:800, fontSize:12, letterSpacing:'0.08em', marginBottom:12 }}>
          SESSION ALIGNMENT STATE
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:8, marginBottom:12 }}>
          {[
            { label:'Confirmed HEAD', value:'pending commit', note:'from git log — never from memory. Multiple sessions (SECTOR-MAPPING-MODULAR onward) delivered but not confirmed committed in available materials — was hardcoded to 5b26d3f (3+ sessions stale), corrected June 19-20, 2026' },
            { label:'Branch', value:'dev', note:'must always be dev' },
            { label:'Smoke', value:'487', note:'smoke-test.sh actual check count, corrected June 19-20, 2026 — was hardcoded 418/418', color:C.green },
            { label:'Current Session', value:'SENATE-HOUSE-CAPTURE-TRANSFORM-ENRICH-REDESIGN — DELIVERED, NOT VALIDATED', note:'6 sessions ran since this previously said "DOC-SYNC complete — PIPELINE-RELOAD is next." See HEAD.md.' },
          ].map(r => (
            <div key={r.label} style={{ background:C.surface, borderRadius:6, padding:'10px 14px',
              border:'1px solid '+C.border }}>
              <div style={{ color:C.muted, fontSize:10, marginBottom:2 }}>{r.label}</div>
              <div style={{ color:r.color||C.green, fontWeight:700, fontSize:14, fontFamily:'monospace' }}>{r.value}</div>
              <div style={{ color:C.muted, fontSize:10, marginTop:2 }}>{r.note}</div>
            </div>
          ))}
        </div>

        {/* File checklist */}
        <div style={{ color:C.info, fontWeight:700, fontSize:11, marginBottom:8 }}>
          FILE UPLOAD CHECKLIST — click once = uploaded · twice = confirmed · again = reset
        </div>
        <div style={{ background:C.border, borderRadius:4, height:6, marginBottom:10 }}>
          <div style={{ height:6, borderRadius:4, background:confirmedCritical?C.green:C.warning,
            width:`${(confirmedCount/SESSION_FILES.length)*100}%`, transition:'width 0.3s' }} />
        </div>
        {SESSION_FILES.map(f => {
          const st = fileStates[f.id];
          const bc = st==='confirmed'?C.green:st==='uploaded'?C.warning:C.border;
          return (
            <div key={f.id} onClick={()=>toggleFile(f.id)} style={{
              display:'flex', alignItems:'flex-start', gap:12, padding:'10px 12px',
              background:st==='confirmed'?C.green+'11':st==='uploaded'?C.warning+'11':C.surface,
              border:'1px solid '+bc, borderRadius:6, marginBottom:6, cursor:'pointer',
            }}>
              <span style={{ fontSize:16, minWidth:24 }}>
                {st==='confirmed'?'✅':st==='uploaded'?'📂':'⬜'}
              </span>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:2 }}>
                  <span style={{ color:C.text, fontWeight:700, fontSize:12, fontFamily:'monospace' }}>{f.name}</span>
                  <Badge color={f.critical?C.danger:C.muted}>{f.critical?'CRITICAL':'OPTIONAL'}</Badge>
                </div>
                <div style={{ color:C.muted, fontSize:10 }}>{f.loc} · Extract: {f.extract}</div>
              </div>
              <span style={{ color:C.muted, fontSize:10, fontFamily:'monospace' }}>
                {st==='confirmed'?'CONFIRMED':st==='uploaded'?'UPLOADED':'MISSING'}
              </span>
            </div>
          );
        })}
        {confirmedCritical && (
          <div style={{ padding:'8px 12px', background:C.green+'22', borderRadius:6,
            border:'1px solid '+C.green+'44', color:C.green, fontSize:12, fontWeight:700, marginTop:6 }}>
            ✅ All critical files confirmed. Claude may proceed.
          </div>
        )}
      </div>

      {/* Gates */}
      <div style={{ background:C.card, borderRadius:10, padding:20, border:'1px solid '+C.border }}>
        <div style={{ color:C.warning, fontWeight:800, fontSize:12, letterSpacing:'0.08em', marginBottom:12 }}>
          GATES — APPLY EVERY SESSION ESPECIALLY UNDER PRESSURE
        </div>
        <div style={{ display:'grid', gap:8 }}>
          {GATES.map(g => (
            <div key={g.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
              background:gateStates[g.id]?C.green+'11':C.surface, borderRadius:6,
              border:'1px solid '+(gateStates[g.id]?C.green:C.border) }}>
              <button onClick={()=>setGateStates(p=>({...p,[g.id]:!p[g.id]}))} style={{
                background:gateStates[g.id]?C.green:C.border, border:'none',
                color:gateStates[g.id]?C.bg:C.muted, borderRadius:20,
                padding:'3px 12px', fontSize:10, fontWeight:700, cursor:'pointer', flexShrink:0,
              }}>{gateStates[g.id]?'ARMED':'ARM'}</button>
              <div>
                <div style={{ color:C.text, fontWeight:700, fontSize:12 }}>Gate {g.id} — {g.name}</div>
                <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>{g.rule}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reconciliation status */}
      <div style={{ background:C.card, borderRadius:10, padding:20, border:'1px solid '+C.border }}>
        <div style={{ color:C.purple, fontWeight:800, fontSize:12, letterSpacing:'0.08em', marginBottom:10 }}>
          PRODUCT_BACKLOG.md RECONCILIATION STATUS
          <span style={{ color:C.warning, fontSize:9, fontWeight:400, marginLeft:8 }}>
            (recounted June 19-20, 2026 — was hardcoded 5/2/22, did not match PRODUCT_BACKLOG.md content)
          </span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
          <div style={{ background:C.danger+'11', border:'1px solid '+C.danger+'44', borderRadius:6, padding:12 }}>
            <div style={{ color:C.danger, fontWeight:700, fontSize:20, textAlign:'center' }}>10</div>
            <div style={{ color:C.muted, fontSize:11, textAlign:'center', marginTop:4 }}>Pending reconciliation</div>
          </div>
          <div style={{ background:C.warning+'11', border:'1px solid '+C.warning+'44', borderRadius:6, padding:12 }}>
            <div style={{ color:C.warning, fontWeight:700, fontSize:20, textAlign:'center' }}>2</div>
            <div style={{ color:C.muted, fontSize:11, textAlign:'center', marginTop:4 }}>Immediate action</div>
          </div>
          <div style={{ background:C.green+'11', border:'1px solid '+C.green+'44', borderRadius:6, padding:12 }}>
            <div style={{ color:C.green, fontWeight:700, fontSize:20, textAlign:'center' }}>8</div>
            <div style={{ color:C.muted, fontSize:11, textAlign:'center', marginTop:4 }}>Closed / reconciled</div>
          </div>
        </div>
        <div style={{ marginTop:10, color:C.muted, fontSize:11 }}>
          Counts recounted from PRODUCT_BACKLOG.md directly (BACKLOG-1 through BACKLOG-22) —
          note 1 item (BACKLOG-17, status "PLANNED — REFACTOR-EDGAR session") doesn't map cleanly
          to these 3 buckets, so the 3 numbers above don't sum to 22; flagging rather than forcing
          a fit. Last reconciliation: May 27, 2026 · DOC-SYNC session (this counter itself was not
          updated since then despite multiple later sessions adding backlog items)
          · Full detail: .claude/PRODUCT_BACKLOG.md
        </div>
      </div>
    </div>
  );
}
